import { TextKey } from '@mews-commander/enums';
import { Stack, Typography } from '@optimus-web/core';
import { CardPaymentMethodStatus, PaymentCardOrigin } from '@mews-commander/data-access';
import { LocalizedLabel } from '@mews-commander/ui';
import { useLocalizeText } from '@mews/framework';
import { PaymentMethodStatusHistoryBodyReservations } from './PaymentMethodStatusHistoryBodyReservations';
import { PaymentMethodStatusHistoryBodyWrapper } from './PaymentMethodStatusHistoryBodyWrapper';

const CardStatusHistoryBodyOriginChannelManager = ({
    propertyName,
    notes,
}: {
    propertyName: CardPaymentMethodStatus['propertyName'];
    notes: CardPaymentMethodStatus['notes'];
}) => {
    const localizeText = useLocalizeText();

    return propertyName ? (
        <Stack vertical>
            <LocalizedLabel
                textKey={TextKey.PaymentMethodCapturedAtProperty}
                variant="secondary"
                substitutes={{
                    source: localizeText({ textKey: TextKey.ChannelManager }),
                    property: propertyName,
                }}
                as="div"
            />
            {/* Displays first two rows from the payment card notes for OTA origin */}
            {notes &&
                notes
                    .split('\n')
                    .slice(0, 2)
                    .map((note) => (
                        <Typography variant="secondary" textStyle="bodyMediumStrong" key={note} as="div">
                            {note}
                        </Typography>
                    ))}
        </Stack>
    ) : null;
};

const CardStatusHistoryBodyOrigin = ({
    status: { source, statusUpdaterProfileName, propertyName, origin, notes },
}: {
    status: CardPaymentMethodStatus;
}) => {
    if (origin === PaymentCardOrigin.ChannelManager) {
        return <CardStatusHistoryBodyOriginChannelManager propertyName={propertyName} notes={notes} />;
    }

    if (origin === PaymentCardOrigin.Commander && propertyName) {
        return (
            <LocalizedLabel
                textKey={TextKey.PaymentMethodCapturedManuallyAtProperty}
                variant="secondary"
                substitutes={{ user: statusUpdaterProfileName, property: propertyName }}
                as="div"
            />
        );
    } else if (origin === PaymentCardOrigin.Commander) {
        return (
            <LocalizedLabel
                textKey={TextKey.PaymentMethodCapturedManually}
                variant="secondary"
                substitutes={{ user: statusUpdaterProfileName }}
                as="div"
            />
        );
    }

    if (source && propertyName) {
        return (
            <LocalizedLabel
                textKey={TextKey.PaymentMethodCapturedByUserAtProperty}
                variant="secondary"
                substitutes={{ source, user: statusUpdaterProfileName, property: propertyName }}
                as="div"
            />
        );
    }

    return (
        <LocalizedLabel
            textKey={TextKey.PaymentMethodUpdatedByUser}
            variant="secondary"
            substitutes={{ user: statusUpdaterProfileName }}
            as="div"
        />
    );
};

const CardStatusHistoryBodyUpdate = ({
    status: { isCardholderNameChanged, isExpirationChanged },
}: {
    status: CardPaymentMethodStatus;
}) => {
    if (isCardholderNameChanged && isExpirationChanged) {
        return <LocalizedLabel textKey={TextKey.PaymentMethodUpdateOwnerAndExpiration} variant="secondary" as="div" />;
    }

    if (isCardholderNameChanged) {
        return <LocalizedLabel textKey={TextKey.PaymentMethodUpdateOwner} variant="secondary" as="div" />;
    }

    if (isExpirationChanged) {
        return <LocalizedLabel textKey={TextKey.PaymentMethodUpdateExpiration} variant="secondary" as="div" />;
    }

    return null;
};

export const CardStatusHistoryBody = ({ status }: { status: CardPaymentMethodStatus }) => (
    <PaymentMethodStatusHistoryBodyWrapper>
        <CardStatusHistoryBodyOrigin status={status} />
        <CardStatusHistoryBodyUpdate status={status} />
        <PaymentMethodStatusHistoryBodyReservations reservations={status.reservationReferenceIdentifiers} />
    </PaymentMethodStatusHistoryBodyWrapper>
);
